/**
 * Shell-хелперы для мигрировавших роутов (волна C,
 * docs/MIGRATION_ROUTER.md §5). Живут в compat/ — этот каталог целиком
 * игнорируется генератором file-роутов (routeFileIgnorePattern).
 */
import type { ComponentType, FC } from 'react';
import { observer } from 'mobx-react';
import CacheDataRoute from '../../CacheDataRoute';
import NotFound404 from '@routes/StaticPages/ErrorPages/NotFound404';
import { useStores } from '../../stores';
import { Redirect, useParams } from './router';

/**
 * 404-страница, как её рендерил легаси catch-all
 * (<PrivateRoute component={NotFound404} is404 />): с Layout и SEO-мета.
 */
export const LegacyNotFound: FC = () => (
  <CacheDataRoute component={NotFound404} is404 />
);

/**
 * Эмуляция regex-параметра v5 `:id(\d+)`: TanStack не умеет regex в path,
 * поэтому не-числовой id отправляем на 404 — ровно как непопадание
 * в Switch в легаси (/lenta/abc, /product/abc и т.п.).
 */
export const numericIdShell = (
  // ComponentType, не ElementType: LayoutProps.component пересекается с
  // RouteProps.component, куда строковые теги ('div' и т.п.) не входят
  Page: ComponentType<any>,
  legacyPath: string,
  canonicalUrlOf?: (id: string) => string
): FC => {
  const Shell: FC = () => {
    const { id } = useParams<{ id: string }>();
    if (!id || !/^\d+$/.test(id)) {
      return <LegacyNotFound />;
    }
    return (
      <CacheDataRoute
        component={Page}
        path={legacyPath}
        canonicalUrl={canonicalUrlOf?.(id)}
      />
    );
  };
  Shell.displayName = `NumericIdShell(${legacyPath})`;
  return Shell;
};

/**
 * Auth-гард кабинета (Фаза 5 — замена PrivateRoute): семантика 1:1 —
 * пока пользователь грузится, ничего не рендерим; авторизован →
 * страница через CacheDataRoute; аноним → Redirect (compat, через
 * legacyHistory). Bar-menu-ветка старого PrivateRoute не переносится:
 * bar-menu-роуты используют CacheDataRoute напрямую.
 */
export const authGuardShell = (
  Page: ComponentType<any>,
  legacyPath: string,
  redirectPathname = '/'
): FC => {
  const Guard: FC = observer(() => {
    const { userStore } = useStores();
    if (userStore.isUserLoading) {
      return null;
    }
    if (userStore.isUserLoggedIn) {
      return <CacheDataRoute component={Page} path={legacyPath} />;
    }
    return <Redirect to={{ pathname: redirectPathname }} />;
  });
  Guard.displayName = `AuthGuardShell(${legacyPath})`;
  return Guard;
};
